import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, BadgeCheck, Building2, UserPlus } from 'lucide-react';

export const NewCompanyCTASection = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const steps = [
    { title: t('home.newco.step1Title'), desc: t('home.newco.step1Desc') },
    { title: t('home.newco.step2Title'), desc: t('home.newco.step2Desc') },
    { title: t('home.newco.step3Title'), desc: t('home.newco.step3Desc') },
  ];

  const feats = [
    { label: t('home.newco.feat1Label'), sub: t('home.newco.feat1Sub') },
    { label: t('home.newco.feat2Label'), sub: t('home.newco.feat2Sub') },
    { label: t('home.newco.feat3Label'), sub: t('home.newco.feat3Sub') },
    { label: t('home.newco.feat4Label'), sub: t('home.newco.feat4Sub') },
    { label: t('home.newco.feat5Label'), sub: t('home.newco.feat5Sub') },
  ];

  return (
    <section
      className="bg-gradient-to-br from-orange-50 via-white to-emerald-50 dark:from-orange-950/30 dark:via-slate-950 dark:to-emerald-950/20 border-t border-b border-slate-200 dark:border-slate-800"
      data-testid="new-company-cta-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-100 dark:bg-orange-950/50 text-orange-700 dark:text-orange-300 text-xs font-semibold mb-4">
              <UserPlus className="h-3.5 w-3.5" />
              {t('home.newco.badge')}
            </div>
            <h2 className="font-manrope text-3xl sm:text-4xl font-bold text-slate-900 dark:text-slate-50 leading-tight">
              {t('home.newco.title')}
            </h2>
            <p className="mt-4 text-slate-600 dark:text-slate-300 leading-relaxed">
              {t('home.newco.subtitle')}
            </p>
            <ul className="mt-6 space-y-3">
              {steps.map((step, i) => (
                <li key={i} className="flex items-start gap-3 text-sm">
                  <div className="flex items-center justify-center h-7 w-7 rounded-full bg-orange-500 text-white font-bold shrink-0 text-xs">{i + 1}</div>
                  <div>
                    <p className="font-semibold text-slate-900 dark:text-slate-100">{step.title}</p>
                    <p className="text-slate-600 dark:text-slate-400">{step.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <button
                onClick={() => navigate('/register/external')}
                className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg shadow-lg shadow-orange-500/30 transition-all hover:shadow-xl hover:-translate-y-0.5"
                data-testid="new-company-cta-register-btn"
              >
                {t('home.newco.ctaRegister')}
                <ArrowRight className="h-4 w-4" />
              </button>
              <button
                onClick={() => navigate('/for-partners')}
                className="inline-flex items-center gap-2 px-6 py-3 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 font-semibold rounded-lg border border-slate-300 dark:border-slate-700 transition-all"
                data-testid="new-company-cta-partners-btn"
              >
                {t('home.newco.ctaPartners')}
              </button>
            </div>
            <p className="mt-4 text-xs text-slate-500 dark:text-slate-400">
              {t('home.newco.disclaimer')}
            </p>
          </div>
          <div className="relative">
            <div className="bg-white dark:bg-slate-900 rounded-2xl p-8 shadow-xl border border-slate-200 dark:border-slate-700">
              <div className="flex items-center gap-3 mb-5">
                <Building2 className="h-8 w-8 text-orange-500" />
                <div>
                  <p className="font-bold text-slate-900 dark:text-slate-50">{t('home.newco.whatYouGet')}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{t('home.newco.freeTier')}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3">
                {feats.map((feat) => (
                  <div key={feat.label} className="flex items-start gap-3 p-2.5 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                    <BadgeCheck className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{feat.label}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">{feat.sub}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="absolute -bottom-3 -right-3 hidden sm:block bg-emerald-500 text-white px-4 py-2 rounded-lg text-xs font-bold shadow-lg rotate-3">
              {t('home.newco.freeBadge')}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
